import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * Source column → rule → claim: a mapping rule cleans, converts and value-maps one source column into a claim on a
 * Lantern attribute. A recipe upstream is Bronze clean-up only; calculations live downstream, in the catalog.
 */
export const MappingRule: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const lbl = { fontSize: 15, fontWeight: 600 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const steps = ['clean · trim, number', 'convert · k → m', 'value map · n/a → empty'];

  return (
    <svg viewBox="0 0 940 350" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Source column → rule → claim" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow-mapping" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      {/* Upstream: the recipe, which only tidies Bronze */}
      <rect x="20" y="20" width="220" height="50" rx="10" fill="none" stroke={color.bronze} strokeWidth="2" />
      <text x="40" y="41" fontSize="14" fontWeight="600" fill={color.bronze}>
        Recipe
      </text>
      <text x="40" y="60" style={sub}>
        Bronze clean-up only
      </text>
      <line x1="130" y1="72" x2="130" y2="104" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-mapping)" />
      {/* The source column */}
      <rect x="20" y="108" width="220" height="150" rx="10" fill="none" stroke={color.bronze} strokeWidth="2.5" />
      <rect x="36" y="124" width="188" height="28" rx="3" fill={color.bronze} opacity=".18" />
      <text x="130" y="143" textAnchor="middle" fontSize="14" fontFamily="monospace" fill={color.fg}>
        Reported_NAV_k
      </text>
      <text x="46" y="178" style={row}>
        412,000
      </text>
      <text x="46" y="206" style={row}>
        n/a
      </text>
      <text x="130" y="240" textAnchor="middle" style={sub}>
        source column
      </text>
      <line x1="242" y1="183" x2="298" y2="183" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-mapping)" />
      {/* The rule */}
      <rect x="302" y="108" width="260" height="150" rx="10" fill="none" stroke={color.accent} strokeWidth="2.5" />
      <text x="432" y="138" textAnchor="middle" style={lbl} fill={color.accent}>
        Mapping rule
      </text>
      {steps.map((step, i) => (
        <g key={step}>
          <rect x="322" y={152 + i * 30} width="220" height="24" rx="3" fill={color.accent} opacity=".12" />
          <text x="332" y={169 + i * 30} style={row}>
            {step}
          </text>
        </g>
      ))}
      <line x1="564" y1="183" x2="620" y2="183" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-mapping)" />
      {/* The claim */}
      <rect x="624" y="108" width="296" height="150" rx="10" fill="none" stroke={color.silver} strokeWidth="2.5" />
      <text x="772" y="138" textAnchor="middle" style={lbl} fill={color.silver}>
        Claim
      </text>
      <text x="772" y="158" textAnchor="middle" style={sub}>
        on a Lantern attribute
      </text>
      <rect x="637" y="176" width="270" height="28" rx="3" fill={color.silver} opacity=".18" />
      <text x="772" y="195" textAnchor="middle" style={row}>
        Fund III · NAV · 30 Jun · 412.0m
      </text>
      <text x="772" y="240" textAnchor="middle" style={sub}>
        one row in Silver
      </text>
      {/* Downstream: the catalog owns every calculation */}
      <line x1="772" y1="260" x2="772" y2="292" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-mapping)" />
      <rect x="624" y="296" width="296" height="46" rx="10" fill="none" stroke={color.gold} strokeWidth="2" />
      <text x="644" y="315" fontSize="14" fontWeight="600" fill={color.gold}>
        Catalog
      </text>
      <text x="644" y="333" style={sub}>
        calculations happen here, never in a rule
      </text>
    </svg>
  );
};
